import MacroBar from './MacroBar';
import WaterTracker from './WaterTracker';

const MEAL_ORDER  = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
const MEAL_ICONS  = { Breakfast: '🌅', Lunch: '🍱', Dinner: '🍽', Snack: '🍎' };
const MEAL_COLORS = {
  Breakfast: 'rgba(255,190,100,0.15)',
  Lunch:     'rgba(100,220,150,0.15)',
  Dinner:    'rgba(100,150,255,0.15)',
  Snack:     'rgba(255,100,180,0.15)',
};

const glass = {
  background:           'rgba(255,255,255,0.07)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border:               '1px solid rgba(255,255,255,0.12)',
  borderRadius:         16,
};

export default function Dashboard({ meals, goals, date, onDateChange, onEditMeal }) {
  const totals = meals.reduce((acc, m) => ({
    calories: acc.calories + m.calories,
    protein:  acc.protein  + m.protein,
    carbs:    acc.carbs    + m.carbs,
    fat:      acc.fat      + m.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const grouped = MEAL_ORDER.reduce((acc, type) => {
    const items = meals.filter(m => m.mealType === type);
    if (items.length > 0) acc[type] = items;
    return acc;
  }, {});

  const mealTotal = (items) => items.reduce((acc, m) => ({
    calories: acc.calories + m.calories,
    protein:  acc.protein  + m.protein,
    carbs:    acc.carbs    + m.carbs,
    fat:      acc.fat      + m.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const sourceIcon = (src) =>
    src === 'USDA' ? '✅' : src === 'AI_ESTIMATED' ? '🤖' : '✏️';

  const formatQuantity = (quantity, unit) => {
    if (!quantity && quantity !== 0) return '';
    return `${quantity}${unit === 'g' || unit === 'ml' ? unit : ' ' + unit}`;
  };

  const calPct = Math.min((totals.calories / goals.dailyCalories) * 100, 100);

  return (
    <div>
      {/* Date picker */}
      <input
        type="date" value={date}
        onChange={e => onDateChange(e.target.value)}
        style={{
          width: '100%', padding: '10px 14px', marginBottom: 16,
          borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)',
          background: 'rgba(255,255,255,0.07)', color: '#fff',
          fontSize: 15, boxSizing: 'border-box',
        }}
      />

      {/* Calorie summary card */}
      <div style={{ ...glass, padding: 24, marginBottom: 16, textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 56, fontWeight: 800, lineHeight: 1,
            background: 'linear-gradient(135deg, #fff 0%, #a0c4ff 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            {Math.round(totals.calories)}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            of {goals.dailyCalories} kcal
          </div>
          <div style={{
            margin: '14px 0 4px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 999, height: 10, overflow: 'hidden'
          }}>
            <div style={{
              width: `${calPct}%`, height: 10, borderRadius: 999,
              background: 'linear-gradient(90deg, #5282ff, #a855f7)',
              boxShadow: '0 0 12px rgba(82,130,255,0.5)',
              transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
            {Math.max(0, Math.round(goals.dailyCalories - totals.calories))} kcal remaining
          </div>
        </div>

	{/* Water Tracker */}
	<WaterTracker date={date} goalMl={goals.dailyWaterMl || 2500} />

        {/* Macro pills */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {[
            { label: 'Protein', value: totals.protein, goal: goals.dailyProtein, color: '#60a5fa' },
            { label: 'Carbs',   value: totals.carbs,   goal: goals.dailyCarbs,   color: '#fbbf24' },
            { label: 'Fat',     value: totals.fat,     goal: goals.dailyFat,     color: '#34d399' },
          ].map(({ label, value, goal, color }) => (
            <div key={label} style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14, padding: '10px 8px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color }}>
                {Math.round(value)}g
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                {label}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
                / {goal}g
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Macro bars */}
      <div style={{ ...glass, padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'rgba(255,255,255,0.8)' }}>
          Macros
        </h3>
        <MacroBar label="Calories" value={totals.calories} goal={goals.dailyCalories} color="#5282ff" unit=" kcal" />
        <MacroBar label="Protein"  value={totals.protein}  goal={goals.dailyProtein}  color="#60a5fa" />
        <MacroBar label="Carbs"    value={totals.carbs}    goal={goals.dailyCarbs}    color="#fbbf24" />
        <MacroBar label="Fat"      value={totals.fat}      goal={goals.dailyFat}      color="#34d399" />
      </div>

      {/* Meals */}
      {meals.length === 0
        ? (
          <div style={{ ...glass, padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🍽</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>
              No meals logged yet
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, marginTop: 6 }}>
              Tap ➕ to add your first meal
            </div>
          </div>
        )
        : MEAL_ORDER.filter(type => grouped[type]).map(type => {
          const items = grouped[type];
          const t     = mealTotal(items);
          return (
            <div key={type} style={{ marginBottom: 14 }}>
              {/* Meal type header */}
              <div style={{
                ...glass,
                background: MEAL_COLORS[type],
                padding: '12px 16px', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {MEAL_ICONS[type]} {type}
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                  {Math.round(t.calories)} kcal · P:{Math.round(t.protein)}g · C:{Math.round(t.carbs)}g · F:{Math.round(t.fat)}g
                </span>
              </div>

              {/* Food items — tappable to edit */}
              <div style={{ borderLeft: '3px solid rgba(255,255,255,0.1)', paddingLeft: 12 }}>
                {items.map((m, i) => (
                  <div
                    key={i}
                    onClick={() => onEditMeal && onEditMeal(m)}
                    style={{
                      ...glass,
                      padding: '12px 16px', marginBottom: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      // Subtle hover hint
                      borderColor: 'rgba(255,255,255,0.15)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(82,130,255,0.5)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
                  >
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <span style={{ fontWeight: 600, fontSize: 14, flex: 1, marginRight: 8 }}>
                        {sourceIcon(m.dataSource)} {m.foodName}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontWeight: 700, fontSize: 14,
                          background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
                          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                        }}>
                          {Math.round(m.calories)} kcal
                        </span>
                        {/* Edit hint */}
                        <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.25)' }}>✏️</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                      {formatQuantity(m.quantity, m.unit || 'g')} · P:{Math.round(m.protein)}g · C:{Math.round(m.carbs)}g · F:{Math.round(m.fat)}g
                      {m.dataSource === 'AI_ESTIMATED' &&
                        <span style={{ marginLeft: 8, color: '#fbbf24' }}>⚠️ AI</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      }
    </div>
  );
}